import { Trans, useLingui } from "@lingui/react";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { Button } from "@warp-ds/react";
import { useEffect, useRef, useState } from "react";
import { useTracking } from "../../utils/pulseHelper.ts";

export const logInToSendMessageEventId = "log-in-to-send-message";
export const logInToReserveCarEventId = "log-in-to-reserve-car";

enum ModalVariations {
  LogInToSendMessage,
  LogInToReserveCar,
}

export const LoginModal = () => {
  const modalRef = useRef<WarpModal>(null);
  const [nextUrl, setNextUrl] = useState("");
  const [variation, setVariation] = useState<ModalVariations>(ModalVariations.LogInToSendMessage);
  const { i18n } = useLingui();

  const { trackLogin, trackCancelLoginModal } = useTracking();

  useEffect(() => {
    const openWith = (variant: ModalVariations) => {
      const { origin, pathname } = window.location;
      const url = new URL(`${origin}/auth/login`);
      const redirectUrl =
        variant === ModalVariations.LogInToSendMessage ? `${origin}${pathname}?contact` : `${origin}${pathname}`;

      url.searchParams.set("redirectUrl", redirectUrl);
      url.searchParams.set("cancelUrl", `${origin}${pathname}`);

      setVariation(variant);
      setNextUrl(url.toString());
      modalRef.current?.open();
    };

    window.addEventListener(logInToSendMessageEventId, () => openWith(ModalVariations.LogInToSendMessage));
    window.addEventListener(logInToReserveCarEventId, () => openWith(ModalVariations.LogInToReserveCar));
    return () => {
      window.removeEventListener(logInToSendMessageEventId, () => openWith(ModalVariations.LogInToSendMessage));
      window.removeEventListener(logInToReserveCarEventId, () => openWith(ModalVariations.LogInToReserveCar));
    };
  }, []);

  const handleCancel = () => {
    modalRef.current?.close();
  };

  const onHidden = () => {
    trackCancelLoginModal();
  };

  const loginModalTitle = i18n.t({ id: "dealer.loginModal.fomo.title", message: "You're not the only one interested" });

  const loginModalText = {
    [ModalVariations.LogInToSendMessage]: i18n.t({
      id: "dealer.loginModal.fomo.description",
      message:
        "Log in and write to the seller before someone else strikes.\n" +
        "You can also save the ad or get notified when something similar appears.",
    }),
    [ModalVariations.LogInToReserveCar]: i18n.t({
      id: "dealer.loginModal.reserve.description",
      message:
        "Log in to reserve the car before someone else does.\n" +
        "You can also save the ad or get notified when something similar appears.",
    }),
  };

  return (
    <Modal ref={modalRef} onHidden={onHidden}>
      <ModalHeader slot="header" title={loginModalTitle} />
      <div slot="content">
        <p>{loginModalText[variation]}</p>
      </div>
      <ModalFooter slot="footer">
        <div className="flex w-full md:w-[50%] ml-auto gap-8">
          <Button secondary quiet fullWidth onClick={handleCancel}>
            <Trans id="dealer.loginModal.cancel" message="Not now" />
          </Button>
          <Button primary fullWidth href={nextUrl} onClick={() => trackLogin()}>
            <Trans id="dealer.loginModal.logIn" message="Log in" />
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
